import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ClickAwayListener, TextField, Popper, Box, List, ListItemButton, useTheme, } from "@mui/material";
import { createRef, useEffect, useLayoutEffect, useMemo, useRef, useState, } from "react";
import { useTranslation } from "react-i18next";
import nerdUII18n from "../i18n";
import { DateTime, Duration } from "luxon";
/**
 *
 * @param {ListWrapperProps} props component props
 * @returns {ReactElement} ListWrapperProps
 */
function ListWrapper(props) {
    const { options, value, match, onChange, format } = props;
    const theme = useTheme();
    const scrollableListRef = createRef();
    useLayoutEffect(() => {
        if (scrollableListRef.current && match)
            scrollableListRef.current?.scrollTo({
                top: 39 * match.index,
                behavior: "auto",
            });
    }, []);
    return (_jsx(Box, { ref: scrollableListRef, sx: {
            maxHeight: 200,
            overflow: "auto",
        }, children: _jsx(List, { children: options.map((option, index) => (_jsx(ListItemButton, { selected: value === option.value, onClick: () => {
                    onChange({
                        value: option.value,
                        duration: durationFromString(option.value, format),
                        isValid: true,
                    });
                }, sx: {
                    backgroundColor: match && match.index === index && value !== option.value
                        ? theme.palette.action.focus
                        : "",
                }, "list-item-index": index, children: option.label }, index))) }) }));
}
/**
 *
 * @param {string} time Time
 * @param {string} format Format
 * @returns {Duration} Duration from string
 */
export function durationFromString(time, format) {
    const dateTime = DateTime.fromFormat(DateTime.now().toFormat("yyyy-LL-dd ") + time, `yyyy-LL-dd ${format == "12" ? "hh:mm a" : "HH:mm"}`);
    return Duration.fromObject({
        hour: dateTime.hour,
        minute: dateTime.minute,
    });
}
/**
 *
 * @param {NerdTimePickerProps} props Props
 * @returns {ReactElement} NerdTimePicker
 */
export default function NerdTimePicker(props) {
    const { format = "24", onChange, value, startTime: _startTime, showDiff = false, textFieldProps, openOnFocus, sameDay, } = props;
    const theme = useTheme();
    const { t } = useTranslation("components", { i18n: nerdUII18n });
    const [anchorEl, setAnchorEl] = useState(null);
    const [match, setMatch] = useState(undefined);
    const [isTimeValid, setIsTimeValid] = useState();
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState(value);
    const startTime = useMemo(() => {
        if (_startTime)
            return _startTime;
        else
            return format == "12" ? "00:00 AM" : "00:00";
    }, [_startTime]);
    const toolTipRef = useRef(null);
    /**
     *
     * @returns {Array<{ value: string; label: string }>} Return options for 24 hour clock.
     */
    function getOptions() {
        const _startTime = durationFromString(startTime, format);
        const startTimeToDateTime = DateTime.now().set({
            hour: _startTime.hours,
            minute: _startTime.minutes,
        });
        let time = startTimeToDateTime;
        const options = [];
        for (let i = 0; i < 96; i++) {
            const diff = time
                .diff(startTimeToDateTime, ["hours", "minutes"])
                .toObject();
            const value = time.toFormat(format === "24" ? "HH:mm" : "hh:mm a");
            options.push({
                label: value +
                    (showDiff
                        ? ` (${diff.hours?.toLocaleString("en-US", {
                            minimumIntegerDigits: 2,
                            useGrouping: false,
                        })}:${diff.minutes?.toLocaleString("en-US", {
                            minimumIntegerDigits: 2,
                            useGrouping: false,
                        })})`
                        : ""),
                value,
            });
            time = time.plus({ minutes: 15 });
            if (sameDay && !time.hasSame(startTimeToDateTime, "day"))
                return options;
        }
        return options;
    }
    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleBlur = () => {
        if (inputValue !== value) {
            if (!isTimeValid) {
                if (match) {
                    onChange({
                        value: match.value,
                        duration: durationFromString(match.value, format),
                        isValid: false,
                    });
                    setInputValue(match.value);
                }
                else {
                    const formatted = tryConvertTimeToRightFormat(inputValue);
                    onChange({
                        value: formatted || inputValue,
                        duration: durationFromString(formatted ? formatted : inputValue || "", format),
                        isValid: formatted === false ? false : true,
                    });
                    setInputValue(formatted ? formatted : inputValue || "");
                }
            }
            else {
                onChange({
                    value: inputValue,
                    duration: inputValue ? durationFromString(inputValue, format) : null,
                    isValid: true,
                });
            }
        }
    };
    /**
     * Try to format time to 12 or 24 hour clock.
     *
     * @param {string} time Time to convert
     * @returns {string | false} Formatted time or false
     */
    function tryConvertTimeToRightFormat(time) {
        let hour = 0;
        let minute = 0;
        if (!time)
            return false;
        const _time = time.replace(/\D/g, "");
        if (_time.length === 1 || _time.length === 2) {
            hour = Number(_time);
        }
        else if (_time.length === 3) {
            hour = Number(_time.charAt(0));
            minute = Number(_time.substring(1, 3));
        }
        else if (_time.length === 4) {
            hour = Number(_time.substring(0, 2));
            minute = Number(_time.substring(2, 4));
        }
        else
            return false;
        const timeToLowerCase = time.toLowerCase();
        if (timeToLowerCase.includes("pm") || timeToLowerCase.includes("am")) {
            const modifier = timeToLowerCase.charAt(timeToLowerCase.indexOf("m") - 1) + "m";
            if (hour === 12) {
                hour = 0;
            }
            if (modifier === "pm") {
                hour += 12;
            }
        }
        if (!Number.isNaN(hour) &&
            !Number.isNaN(minute) &&
            hour >= 0 &&
            hour <= 24 &&
            minute >= 0 &&
            minute <= 59) {
            return DateTime.now()
                .set({ hour, minute })
                .toFormat(format === "12" ? "hh:mm a" : "HH:mm");
        }
        else
            return false;
    }
    /**
     *
     * @param {KeyboardEvent<HTMLDivElement>} e Key event
     */
    function keyPress(e) {
        if (e.key === "Enter") {
            if (anchorEl)
                handleClose();
        }
        else if (e.key === "ArrowUp") {
            setAnchorEl(e.currentTarget);
            e.preventDefault();
            e.stopPropagation();
            let newValue = "";
            if (match !== undefined && match.index > 0)
                newValue = options[match.index - 1].value;
            else if (match?.index === 0 || match === undefined)
                newValue = options[options.length - 1].value;
            onChange({
                value: newValue,
                duration: durationFromString(newValue, format),
                isValid: true,
            });
        }
        else if (e.key === "ArrowDown") {
            e.preventDefault();
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
            let newValue = "";
            if (match !== undefined && match.index < options.length - 1)
                newValue = options[match.index + 1].value;
            else if (match?.index === options.length - 1 || match === undefined)
                newValue = options[0].value;
            onChange({
                value: newValue,
                duration: durationFromString(newValue, format),
                isValid: true,
            });
        }
    }
    /**
     * Check if user entered time match to one or multiples options.
     *
     * @param {string} time User entered time
     * @returns {Array<{value: string; index: number;}>} Matched options
     */
    function guessTime(time) {
        const matches = [];
        for (let index = 0; index < options.length; index++) {
            const option = options[index];
            const _option = option.value.replace(/\D/g, "");
            const _time = time.replace(/\D/g, "");
            if (option.value == time)
                return [{ value: option.value, index }];
            if (_option.startsWith(_time) || _option.endsWith(_time)) {
                const timeToLowerCase = time.toLowerCase();
                const optionToLowerCase = option.value.toLowerCase();
                /*If time is 'hh:mm a' format, check if modifiers match.
                     For example, if time is 130 pm, we want to return 01:30 PM instead of 01:30 AM.*/
                if ((timeToLowerCase.includes("pm") || timeToLowerCase.includes("am")) &&
                    format === "12") {
                    const timeModifier = timeToLowerCase.charAt(timeToLowerCase.indexOf("m") - 1) + "m";
                    const optionModifier = optionToLowerCase.charAt(optionToLowerCase.indexOf("m") - 1) + "m";
                    if (timeModifier === optionModifier) {
                        return [{ value: option.value, index }];
                    }
                }
                else {
                    return [{ value: option.value, index }];
                }
            }
            else if (_option.includes(_time)) {
                matches.push({ value: option.value, index });
            }
        }
        return matches;
    }
    /**
     * Check if time is valid.
     *
     * @param {string} value Time to validate
     * @returns {boolean} Returns true if time match the following formats : "hh:mm a" or "HH:mm"
     */
    function timeValidator(value) {
        if (value === "")
            return false;
        else if (!value.match(/^(1[0-2]|0[1-9]):[0-5][0-9]\s(AM|PM)$/) &&
            format === "12")
            return false;
        else if (!value.match(/^([01][0-9]|2[0-3]):([0-5][0-9])$/) &&
            format === "24")
            return false;
        else
            return true;
    }
    useEffect(() => {
        if (match !== undefined) {
            const elem = toolTipRef.current?.querySelector(`[list-item-index="${match.index}"]`);
            if (elem && elem.scrollIntoView)
                elem.scrollIntoView({
                    behavior: "auto",
                    inline: "nearest",
                    block: "nearest",
                });
        }
    }, [match]);
    useEffect(() => {
        if (inputValue) {
            const result = guessTime(inputValue);
            if (result.length > 0)
                setMatch(result[result.length - 1]);
            else
                setMatch(undefined);
            setIsTimeValid(timeValidator(inputValue));
        }
    }, [inputValue]);
    useEffect(() => {
        if (value !== inputValue) {
            setInputValue(value);
        }
    }, [value]);
    useEffect(() => {
        const formatted = tryConvertTimeToRightFormat(inputValue);
        if (formatted) {
            setInputValue(formatted);
        }
        setOptions(getOptions());
    }, [format]);
    useEffect(() => {
        if (inputValue) {
            const result = guessTime(inputValue);
            if (result.length > 0)
                setMatch(result[result.length - 1]);
            else
                setMatch(undefined);
        }
    }, [options]);
    useEffect(() => {
        setOptions(getOptions());
    }, [startTime, showDiff]);
    return (_jsx(ClickAwayListener, { onClickAway: handleClose, children: _jsxs("div", { style: {
                width: format === "12" ? 80 : 45,
            }, children: [_jsx(TextField, { autoComplete: "off", size: "small", sx: {
                        width: format === "12" ? 80 : 45,
                    }, label: t("nerdDateTimePicker.time"), variant: "standard", ...textFieldProps, onClick: (e) => {
                        textFieldProps?.onClick?.(e);
                        if (!textFieldProps?.disabled) {
                            setAnchorEl(e.currentTarget);
                        }
                    }, value: inputValue || "", onChange: (e) => {
                        setInputValue(e.target.value);
                        textFieldProps?.onChange?.(e);
                    }, onBlur: (e) => {
                        handleBlur();
                        textFieldProps?.onBlur?.(e);
                    }, onFocus: (e) => {
                        textFieldProps?.onFocus?.(e);
                        if (openOnFocus) {
                            setAnchorEl(e.currentTarget);
                        }
                    }, onKeyDown: (e) => {
                        keyPress(e);
                        if (!anchorEl)
                            textFieldProps?.onKeyDown?.(e);
                    }, error: isTimeValid === false || textFieldProps?.error ? true : false, helperText: isTimeValid === false && !anchorEl
                        ? t("nerdDateTimePicker.invalid_time")
                        : textFieldProps?.helperText
                            ? textFieldProps?.helperText
                            : "" }), _jsx(Popper, { ref: toolTipRef, open: anchorEl === null ? false : true, anchorEl: anchorEl, sx: {
                        zIndex: theme.zIndex.modal,
                        boxShadow: theme.shadows[5],
                        borderRadius: "4px",
                        backgroundColor: "inherit",
                        width: "fit-content",
                    }, children: _jsx(ListWrapper, { format: format, value: inputValue, match: match, onChange: (e) => {
                            onChange(e);
                            setInputValue(e.value);
                            setAnchorEl(null);
                        }, options: options, setAnchorEl: (newValue) => setAnchorEl(newValue) }) })] }) }));
}
